import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchTrialRequests, updateTrialRequest, adminResendCredentials } from "../redux/reducers/trialSlice";
import { FiCheck, FiX, FiClock, FiUser, FiMail, FiCalendar, FiRefreshCw } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";

const statusColors = {
  active: "bg-green-100 text-green-700",
  expired: "bg-yellow-100 text-yellow-700",
  rejected: "bg-red-100 text-red-700",
};

const TrialRequests = () => {
  const dispatch = useDispatch();
  const { requests, loading } = useSelector((state) => state.trial);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchTrialRequests());
  }, [dispatch]);

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  const handleStatus = (id, status) => {
    dispatch(updateTrialRequest({ id, status }));
  };

  const handleResend = async (userId, name) => {
    try {
      await dispatch(adminResendCredentials(userId)).unwrap();
      toast.success(`Credentials resent to ${name}`);
    } catch (err) {
      toast.error(err || "Failed to resend credentials");
    }
  };

  const fmt = (date) => date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="p-4">
        <Header
          title="Trial Requests"
          subTitle="Admin"
          description="View and manage all trial account requests submitted through the landing page."
        />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Requests", value: requests.length, color: "text-gray-800" },
            { label: "Active Trials", value: requests.filter((r) => r.status === "active").length, color: "text-green-600" },
            { label: "Logged In", value: requests.filter((r) => r.firstLoginAt).length, color: "text-blue-600" },
            { label: "Credentials Sent", value: requests.filter((r) => r.credentialsSent).length, color: "text-purple-600" },
          ].map((s) => (
            <div key={s.label} className="border-2 rounded-2xl p-4 text-center">
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-gray-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {["all", "active", "expired", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                filter === f ? "bg-purple-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f} {f === "all" ? `(${requests.length})` : `(${requests.filter((r) => r.status === f).length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-400 text-sm py-6 text-center">Loading trial requests...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-2xl">
            <FiUser className="text-gray-300 text-4xl mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No trial requests found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((req) => (
              <div key={req._id} className="border-2 rounded-2xl p-5 bg-white">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-gray-800">{req.name}</h3>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusColors[req.status]}`}>
                        {req.status}
                      </span>
                      {req.credentialsSent && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-0.5 rounded-full">Credentials Sent</span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5"><FiMail className="text-xs" /> {req.email}</span>
                      <span className="flex items-center gap-1.5"><FiUser className="text-xs" /> {req.organization}</span>
                      <span className="flex items-center gap-1.5"><FiCalendar className="text-xs" /> Submitted: {fmt(req.createdAt)}</span>
                      <span className="flex items-center gap-1.5">
                        <FiClock className="text-xs" />
                        First Login: {req.firstLoginAt ? fmt(req.firstLoginAt) : <span className="text-yellow-600">Not yet</span>}
                      </span>
                      {req.lastLoginAt && (
                        <span className="flex items-center gap-1.5"><FiClock className="text-xs" /> Last Login: {fmt(req.lastLoginAt)}</span>
                      )}
                      {req.userId && (
                        <span className="text-xs text-gray-400">Account: {req.userId.paymentStatus || "—"}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0 flex-wrap">
                    {req.userId?._id && (
                      <button
                        onClick={() => handleResend(req.userId._id, req.name)}
                        title="Resend login credentials"
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium"
                      >
                        <FiRefreshCw /> Resend
                      </button>
                    )}
                    {req.status !== "active" && (
                      <button
                        onClick={() => handleStatus(req._id, "active")}
                        title="Mark Active"
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium"
                      >
                        <FiCheck /> Activate
                      </button>
                    )}
                    {req.status !== "rejected" && (
                      <button
                        onClick={() => handleStatus(req._id, "rejected")}
                        title="Reject"
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium"
                      >
                        <FiX /> Reject
                      </button>
                    )}
                    {req.status !== "expired" && (
                      <button
                        onClick={() => handleStatus(req._id, "expired")}
                        title="Mark Expired"
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg text-xs font-medium"
                      >
                        <FiClock /> Expire
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TrialRequests;
